import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import RadioGroup, { type RadioGroupProps } from '@mui/material/RadioGroup';
import { forwardRef } from 'react';

import { type RadioProps, Radio } from '../Radio';
import type { OptionSelectionProps } from '../types';

type RadioSelectionProps = Pick<
  RadioGroupProps,
  'value' | 'onChange' | 'defaultValue' | 'row' | 'sx'
> & {
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  options: (Omit<OptionSelectionProps, 'label'> & {
    label: React.ReactNode;
    disabled?: boolean;
    endAdornment?: React.ReactNode;
  })[];
  radioProps?: RadioProps;
};

const RadioSelection = forwardRef((props: RadioSelectionProps, ref) => {
  const {
    value,
    defaultValue,
    options,
    onChange,
    row: hasRow,
    label,
    helperText,
    radioProps,
    ...restProps
  } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    onChange?.(event, value);
  };

  return (
    <FormControl {...restProps}>
      {label ? (
        <>
          {!hasRow ? (
            <Box
              sx={{
                mb: 1,
              }}
            />
          ) : null}
          <InputLabel>{label}</InputLabel>
          <Box
            sx={{
              mb: 5,
            }}
          />
        </>
      ) : null}
      <RadioGroup
        defaultValue={defaultValue}
        //FIXME:value is possibly to be undefined
        value={value ?? ''}
        onChange={handleChange}
        row={hasRow}
        ref={ref}
      >
        {options.map(({ value, label, disabled, endAdornment }, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
            <Radio
              value={value}
              label={label}
              disabled={disabled}
              sx={{ mr: hasRow ? 6.5 : undefined }}
              {...radioProps}
            />
            {endAdornment}
          </Box>
        ))}
      </RadioGroup>
      {helperText ? (
        <FormHelperText sx={{ mt: !hasRow ? 1.5 : 0 }}>{helperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
});

RadioSelection.displayName = 'RadioSelection';

export { RadioSelection };
export type { RadioSelectionProps };
